import { enumsTypes, ITEM_STATUS_OPTIONS } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { CURRENCY_MAP } from "@shared/constants/enums";
import { InteractionLogger } from "@shared/lib/analytics/internal/UserInteractionLogger";
import { useStore } from "@shared/store/useStore";
import {
  addCustomMetric,
  startMeasurement,
  trackError as trackDatadogError,
} from "@shared/utils/datadog/datadog-timings";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import Cookies from "js-cookie";
import { ModifyBasket, TicketBasket } from "../../types";
import { TicketItemGroupType } from "../../types/common/ticket-item-group/ticket-item-group-type";
import {
  isItemEligibleForProtection,
  isRefundProtectionMetricsDryRun,
  logDryRunMetric,
} from "./helpers";
import { TGProxy } from "./TGProxy";

enum RefundProtectionGetValues {
  API_KEY = "apiKey",
  CLEAR_SELECTION = "clearSelection",
  CURRENCY = "currency",
  IS_FORM_COMPLETE = "isFormComplete",
  IS_PROTECTED = "isProtected",
  LINKS = "links",
  LOCALE = "locale",
  PERSIST = "persist",
  PREMIUM_HASH = "premiumHash",
  QUOTE = "quote",
  QUOTE_HASH = "quoteHash",
  QUOTE_LITERAL = "quoteLiteral",
  SANDBOX = "sandbox",
  TOKEN = "token",
}

declare global {
  interface Window {
    __teakLoadStart?: number;
    __teakLoadEnd?: number;
    __teakLoadError?: boolean;
    tg: {
      get: (key: RefundProtectionGetValues) => string;
      update: (config: { items: unknown[] }) => void;
      optInCb: () => void;
      optOutCb: () => void;
      updatedCb: () => void;
      isFormComplete: () => boolean;
      clear: () => void;
      focus: () => void;
      isProtected: boolean;
      removePersistedSession: () => void;
      (...args: unknown[]): void;
    };
  }
}

export class RefundProtectionManager {
  private isConfigured = false;
  private hasWidgetLoaded = false;
  private interactionLogger = new InteractionLogger();
  private modifyBasket: ModifyBasket;
  private onFormComplete?: () => void;
  private onError?: (error?: unknown) => void;
  private onWidgetChange?: () => (() => void) | null;
  private onLoaded?: () => void;
  private tg!: typeof window.tg;
  private unsubscribe: () => void;
  private itemsHash = "";
  private currency: enumsTypes.CurrencyMapIdType = CURRENCY_MAP.CAD.id;
  private isOptedIn = false;
  private locale = `${Cookies.get("NEXT_LOCALE") || "en"}_CA`;
  private trackingEnabled = false;
  private basket: TicketBasket;
  private configRetryCount = 0;
  private configRetryTimeout?: ReturnType<typeof setTimeout>;
  private stopWidgetRenderMeasurement?: (
    context?: Record<string, unknown>
  ) => void;
  private configureStartTime?: number;

  private readonly MAX_RETRIES = 3;
  // the widget script should load before this is mounted, so we dont need a huge delay
  private readonly RETRY_DELAYS = [100, 200, 500]; // Exponential backoff in ms - longest wait is 800ms
  // Static methods
  static buildItemsFromItemGroups(itemGroups: TicketItemGroupType[]) {
    return (
      itemGroups
        .filter(isItemEligibleForProtection)
        // sort by item group id as item group order is not guaranteed.
        // this is for itemsHash to be consistent across different item group sort orders.
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
        .map((itemGroup) =>
          Array.from({ length: itemGroup.quantity ?? 0 }, () => ({
            cost: itemGroup.covered_amount_per_item_for_protection ?? 0,
          }))
        )
        .flat()
    );
  }

  // Constructor
  constructor(
    basketType: DataProvider,
    modifyBasket?: ModifyBasket,
    onFormComplete?: () => void,
    onError?: (error: unknown) => void,
    isEligible = false,
    shouldShow = false
  ) {
    this.modifyBasket =
      modifyBasket || (() => Promise.resolve({} as TicketBasket));
    this.onFormComplete = onFormComplete || undefined;
    this.onError = onError || undefined;

    if (basketType === DataProvider.Public) {
      this.trackingEnabled = true;
    }

    // Initialize tg with proxy
    this.initializeTg();

    this.basket = useStore.getState().getBasket(basketType);

    // Track exposure based on eligibility and whether widget should be shown
    if (!isEligible) {
      // Basket doesn't qualify for protection (items, credits, gateway, etc.)
      this.trackingEnabled = false;
      this.trackProtectionExposure({
        isEligible: false,
        isWidgetShown: false,
        notShownReason: "basket_not_eligible",
      });
    } else if (!shouldShow) {
      // Basket qualifies, but widget not shown (A/B test control group)
      this.trackingEnabled = false;
      this.trackProtectionExposure({
        isEligible: true,
        isWidgetShown: false,
        notShownReason: "ab_test_control",
      });
    }
    // If isEligible && shouldShow, tracking happens in handleLoaded/handleError/configure

    // subscribe to the basket changes in zustand
    this.unsubscribe = useStore.subscribe((state) => {
      const basket = state.getBasket(basketType);
      this.basket = basket;
      if (
        basket &&
        basket.id &&
        basket.item_groups &&
        basket.status === ITEM_STATUS_OPTIONS.PENDING.id
      ) {
        this.update({
          items: RefundProtectionManager.buildItemsFromItemGroups(
            basket?.item_groups ?? []
          ),
        });
      }
    });
  }

  // Lifecycle methods
  public destroy() {
    this.clear();
    this.unsubscribe();
  }

  /**
   * Clear the refund protection widget and reset the manager
   * to its initial state. This will also remove the widget from the page.
   */
  public clear() {
    this.itemsHash = "";
    this.tg.clear();
    this.isConfigured = false;
    this.hasWidgetLoaded = false;
    this.isOptedIn = false;
    this.trackingEnabled = false;
    this.basket = {} as TicketBasket;
    this.configRetryCount = 0;
    this.configureStartTime = undefined;
    this.stopWidgetRenderMeasurement = undefined;

    if (this.configRetryTimeout) {
      clearTimeout(this.configRetryTimeout);
      this.configRetryTimeout = undefined;
    }
  }

  /**
   * Initialize or reinitialize the tg object with proxy wrapper
   */
  private initializeTg() {
    // Wrap tg with TGProxy to intercept all property accesses and method calls
    const rawTg =
      typeof window !== "undefined" && window.tg
        ? window.tg
        : ({} as typeof window.tg);

    const tgProxy = new TGProxy(rawTg);
    this.tg = tgProxy.getProxy();
  }

  /**
   * Check if the tg object is available on window
   */
  private static isTgAvailable(): boolean {
    return typeof window !== "undefined" && typeof window.tg === "function";
  }

  /**
   * Returns whether the widget has successfully loaded
   */
  public isWidgetLoaded(): boolean {
    return this.hasWidgetLoaded;
  }

  // Public configuration methods
  public configure(config: {
    apiKey: string;
    sandbox: boolean;
    items: unknown[];
    currency: enumsTypes.CurrencyMapIdType;
  }) {
    this.currency = config.currency || CURRENCY_MAP.CAD.id;

    if (this.isConfigured) {
      return;
    }

    // Track when configure was first called (for script load timing)
    if (!this.configureStartTime) {
      this.configureStartTime = performance.now();
    }

    // Check if tg is available, if not, retry
    if (!RefundProtectionManager.isTgAvailable()) {
      if (this.configRetryCount < this.MAX_RETRIES) {
        const delay = this.RETRY_DELAYS[this.configRetryCount] || 4000;
        const attemptNumber = this.configRetryCount + 1;
        logAppInfo(
          `window.tg not available, retrying configuration in ${delay}ms (attempt ${attemptNumber}/${this.MAX_RETRIES})`,
          {
            category: "refundProtection",
            data: { attempt: attemptNumber, delay },
          }
        );

        // Track retry in Datadog
        if (isRefundProtectionMetricsDryRun()) {
          logDryRunMetric("metric", "refund_protection_script_retry", {
            attempt: attemptNumber,
            delay_ms: delay,
            max_retries: this.MAX_RETRIES,
          });
        } else {
          addCustomMetric("refund_protection_script_retry", attemptNumber, {
            attempt: attemptNumber,
            delay_ms: delay,
            max_retries: this.MAX_RETRIES,
          });
        }

        this.configRetryTimeout = setTimeout(() => {
          this.configRetryCount += 1;
          this.initializeTg(); // Reinitialize the proxy with latest window.tg
          this.configure(config);
        }, delay);
        return;
      }

      // Max retries reached - track failure in Datadog
      const scriptLoadDuration = this.configureStartTime
        ? performance.now() - this.configureStartTime
        : undefined;

      if (isRefundProtectionMetricsDryRun()) {
        logDryRunMetric("metric", "refund_protection_script_load_failed", {
          retries_attempted: this.MAX_RETRIES,
          duration_ms: scriptLoadDuration,
        });
        logDryRunMetric("error", "refund_protection_script_failed_to_load", {
          component: "RefundProtectionManager",
          retries_attempted: this.MAX_RETRIES,
          duration_ms: scriptLoadDuration,
        });
      } else {
        addCustomMetric("refund_protection_script_load_failed", undefined, {
          retries_attempted: this.MAX_RETRIES,
          duration_ms: scriptLoadDuration,
        });

        trackDatadogError("RefundProtection script failed to load", {
          component: "RefundProtectionManager",
          retries_attempted: this.MAX_RETRIES,
          duration_ms: scriptLoadDuration,
        });
      }

      logAppInfo("window.tg not available after max retries", {
        category: "refundProtection",
        level: "error",
        data: { maxRetries: this.MAX_RETRIES },
      });
      this.trackProtectionExposure({
        isEligible: true,
        isWidgetShown: false,
        notShownReason: "widget script failed to load after retries",
      });
      this.onError?.();
      return;
    }

    // Script is now available - track SDK load time (network + parse + execute)
    /* eslint-disable no-underscore-dangle */
    if (window.__teakLoadStart && window.__teakLoadEnd) {
      const sdkLoadTime = window.__teakLoadEnd - window.__teakLoadStart;
      /* eslint-enable no-underscore-dangle */

      if (isRefundProtectionMetricsDryRun()) {
        logDryRunMetric("metric", "refund_protection_sdk_load", {
          duration_ms: sdkLoadTime,
        });
      } else {
        addCustomMetric("refund_protection_sdk_load", sdkLoadTime, {
          duration_ms: sdkLoadTime,
        });
      }
    }

    // Track script load time from component's perspective (wait time)
    const scriptLoadDuration = this.configureStartTime
      ? performance.now() - this.configureStartTime
      : 0;

    if (isRefundProtectionMetricsDryRun()) {
      logDryRunMetric("metric", "refund_protection_script_load", {
        retries_needed: this.configRetryCount,
        duration_ms: scriptLoadDuration,
      });
      // Store start time for dry run widget render measurement
      const widgetRenderStartTime = performance.now();
      this.stopWidgetRenderMeasurement = (
        context?: Record<string, unknown>
      ) => {
        const duration = performance.now() - widgetRenderStartTime;
        logDryRunMetric("timing", "refund_protection_widget_render", {
          duration_ms: duration,
          ...context,
        });
      };
    } else {
      addCustomMetric("refund_protection_script_load", scriptLoadDuration, {
        retries_needed: this.configRetryCount,
        duration_ms: scriptLoadDuration,
      });

      // Start widget render measurement (will be stopped in handleLoaded)
      this.stopWidgetRenderMeasurement = startMeasurement(
        "refund_protection_widget_render"
      );
    }

    // tg is available, attempt to configure
    this.itemsHash = JSON.stringify(config.items);
    this.tg("configure", {
      ...config,
      locale: this.locale,
      optInCb: this.handleOptIn.bind(this),
      optOutCb: this.handleOptOut.bind(this),
      updatedCb: this.handleUpdated.bind(this),
      onErrorCb: this.handleError.bind(this), // Widget calls this on error, doesn't throw
      loadedCb: this.handleLoaded.bind(this),
    });
    this.isConfigured = true;
    this.configRetryCount = 0; // Reset retry count on success
  }

  public update(config: { items: unknown[] }) {
    if (!this.tg || !this.isConfigured || config.items.length === 0) {
      return;
    }
    const currentHash = JSON.stringify(config.items);

    if (currentHash !== this.itemsHash) {
      this.itemsHash = currentHash;

      this.tg("update", {
        ...config,
        currency: this.currency,
        locale: this.locale,
      });
    }
  }

  public updateModifyBasket(modifyBasket: ModifyBasket) {
    this.modifyBasket = modifyBasket;
  }

  public updateOnFormComplete(onFormComplete: () => void) {
    this.onFormComplete = onFormComplete;
  }

  public updateOnError(onError: (error: unknown) => void) {
    this.onError = onError;
  }

  public updateOnWidgetChange(onWidgetChange: () => (() => void) | null): void {
    this.onWidgetChange = onWidgetChange;
  }

  public updateOnLoaded(onLoaded: () => void) {
    this.onLoaded = onLoaded;
  }

  public enableInternalTracking() {
    this.trackingEnabled = true;
  }

  public disableInternalTracking() {
    this.trackingEnabled = false;
  }

  public trackProtectionExposure({
    isEligible,
    isWidgetShown,
    notShownReason,
  }: {
    isEligible: boolean;
    isWidgetShown: boolean;
    notShownReason: string | null;
  }) {
    this.interactionLogger.refundProtectionExposure({
      basket: this.basket,
      additionalParams: {
        source_step: "payment",
        refund_protection_eligible: isEligible,
        refund_protection_shown: isWidgetShown,
        not_shown_reason:
          !this.isConfigured && isEligible
            ? "widget not configured"
            : notShownReason,
        refund_protection_amount: this.isConfigured
          ? this.tg.get(RefundProtectionGetValues.QUOTE) ?? null
          : null,
      },
    });
  }

  // Public getter methods
  public getToken(): string {
    return this.tg.get(RefundProtectionGetValues.TOKEN) || "";
  }

  // Private helper methods
  private modifyBasketWithToken() {
    this.modifyBasket(
      {
        refund_protection_token: this.getToken(),
        refund_protection_opt_in: this.isOptedIn,
      },
      { shouldPersist: true }
    );
  }

  private trackAddProtection(quantity: number) {
    this.interactionLogger.addToCart({
      item: {
        item_type: "protection",
      },
      count: quantity,
      venue: this.basket.payment_venue,
      basket: this.basket,
    });
  }

  private handleLoaded() {
    this.hasWidgetLoaded = true;

    // Stop widget render measurement and report to Datadog
    if (this.stopWidgetRenderMeasurement) {
      this.stopWidgetRenderMeasurement({
        success: true,
        retries_needed: this.configRetryCount,
      });
      this.stopWidgetRenderMeasurement = undefined;
    }

    this.onLoaded?.();
    if (this.trackingEnabled) {
      // if we loaded the widget, then it means there is an eligible item
      this.trackProtectionExposure({
        isEligible: true,
        isWidgetShown: true,
        notShownReason: null,
      });
    }
  }

  private handleOptIn() {
    this.isOptedIn = true;
    this.onFormComplete?.();
    this.modifyBasketWithToken();
    if (this.trackingEnabled) {
      this.trackAddProtection(JSON.parse(this.itemsHash).length);
    }
  }

  private handleOptOut() {
    this.onFormComplete?.();
    this.isOptedIn = false;
    this.modifyBasketWithToken();
    if (this.trackingEnabled) {
      this.trackAddProtection(0);
    }
  }

  private handleUpdated() {
    this.modifyBasketWithToken();
    if (this.trackingEnabled) {
      this.trackAddProtection(JSON.parse(this.itemsHash).length);
    }
    // Reapply click propagation fix after widget update
    this.onWidgetChange?.();
  }

  private handleError(error?: unknown): void {
    // Stop widget render measurement with error state
    if (this.stopWidgetRenderMeasurement) {
      this.stopWidgetRenderMeasurement({
        success: false,
        error_type: "widget_error_callback",
      });
      this.stopWidgetRenderMeasurement = undefined;
    }

    // Track error in Datadog
    if (isRefundProtectionMetricsDryRun()) {
      logDryRunMetric("error", "refund_protection_widget_error", {
        component: "RefundProtectionManager",
        error_source: "widget_error_callback",
        error_message:
          error instanceof Error
            ? error.message
            : "RefundProtection widget error",
      });
    } else {
      trackDatadogError(
        error instanceof Error
          ? error
          : new Error("RefundProtection widget error"),
        {
          component: "RefundProtectionManager",
          error_source: "widget_error_callback",
        }
      );
    }

    logAppInfo("Error loading protection widget", {
      category: "refundProtection",
      level: "error",
      data: { error },
    });
    if (error) {
      recordAppError(error);
    }
    // Widget failed - track exposure and opt out
    this.trackProtectionExposure({
      isEligible: true,
      isWidgetShown: false,
      notShownReason: "widget error callback triggered",
    });
    this.handleOptOut();
    this.onError?.(error);
  }
}
